// Dismissal of the install banner: a timestamp in localStorage and a cool-down.
// Device-level like the theme, never sent to the backend, survives sign-out.
// Every access is guarded: blocked storage means a dismissal lasts for the
// session only. See [[install-app-banner.spec]] § Dismissal.

export const INSTALL_BANNER_DISMISSED_KEY = "otus:install-banner-dismissed-at";

/** How long a dismissal hides the banner. */
export const INSTALL_BANNER_COOLDOWN_DAYS = 14;
export const INSTALL_BANNER_COOLDOWN_MS =
  INSTALL_BANNER_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

// Kept in memory as well, so that with blocked storage a dismissal still holds
// across the banner remounting as the user moves between screens.
let memoryDismissedAt: number | null = null;

/** Forgets the in-memory dismissal. For tests. */
export function resetDismissalMemory(): void {
  memoryDismissedAt = null;
}

/**
 * When the banner was last dismissed, in ms since the epoch, or null if never
 * (or if the stored value is unusable: not a number, zero or negative).
 */
export function readDismissedAt(): number | null {
  try {
    const raw = window.localStorage.getItem(INSTALL_BANNER_DISMISSED_KEY);
    if (raw !== null) {
      const at = Number(raw);
      if (Number.isFinite(at) && at > 0) return at;
    }
  } catch {
    // Storage blocked: fall back to the in-memory value.
  }
  return memoryDismissedAt;
}

export function writeDismissedAt(at: number): void {
  memoryDismissedAt = at;
  try {
    window.localStorage.setItem(INSTALL_BANNER_DISMISSED_KEY, String(at));
  } catch {
    // Storage blocked: the in-memory value covers this session.
  }
}

/**
 * Whether a dismissal at `dismissedAt` still hides the banner at `now`. A
 * timestamp in the future (the clock moved back) counts as never dismissed, so
 * a bad value cannot hide the banner for longer than the cool-down.
 */
export function isCoolingDown(dismissedAt: number | null, now: number): boolean {
  if (dismissedAt === null || dismissedAt > now) return false;
  return now - dismissedAt < INSTALL_BANNER_COOLDOWN_MS;
}
